export const fileTypesString = 'image/*, audio/mpeg, audio/x-m4a, video/mp4, video/quicktime, text/csv, text/plain, application/pdf, application/vnd.ms-excel, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.oasis.opendocument.text'

export const fileTypesWithExtensions = {
  image: ['jpg', 'jpeg', 'jpe', 'jif', 'jfif', 'jfi', 'png', 'bmp', 'gif', 'webp', 'tiff', 'tif', 'bmp', 'svg', 'svgz'],
  'text/csv': ['csv'],
  'text/plain': ['txt', 'text', 'asc'],
  'text/xml': ['xml', 'xsd', 'rng'],
  'application/pdf': ['pdf'],
  'application/ms-excel': ['xlsx', 'xls', ''],
  'application/ms-word': ['doc', 'docx'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['odc'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['ods'],
  'application/vnd.oasis.opendocument.text': ['odt'],
}

export const extensionMatchesFileType = (fileExt, fileType) => {
  let isMatch = false
  Object.keys(fileTypesWithExtensions).forEach(type => {
    if (type === fileType || fileType.includes(type)) isMatch = fileTypesWithExtensions[type].some(ext => ext === fileExt)
  })
  return isMatch
}

const browserSupportedMimeTypes = [
  'text/plain',
  'text/xml',
  'image/bmp',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/tiff',
  'audio/basic',
  'audio/mpeg',
  'audio/x-aiff',
  'audio/x-wav',
  'video/mpeg',
  'video/mp4',
  'video/x-ms-asf',
  'video/x-msvideo',
  'application/pdf',
]

export const isMimeTypeSupportedByBrowser = mimeType => {
  if (!mimeType || !(typeof mimeType === 'string')) return false
  return browserSupportedMimeTypes.includes(mimeType.toLowerCase())
}

// getMimeType Idea from vue-cropper
export const getMimeType = (file, fallback = null) => {
  const byteArray = (new Uint8Array(file)).subarray(0, 4)
  let header = ''
  for (let i = 0; i < byteArray.length; i++) {
    header += byteArray[i].toString(16)
  }
  switch (header) {
    case '89504e47':
      return 'image/png'
    case '47494638':
      return 'image/gif'
    case 'ffd8ffe0':
    case 'ffd8ffe1':
    case 'ffd8ffe2':
    case 'ffd8ffe3':
    case 'ffd8ffe8':
      return 'image/jpeg'
    default:
      return fallback
  }
}

/**
 * This function enables the browser to trigger a file URL for the file download in the
 * browser or on the user's device saved to disk.
 * Arguments:
 *  contentURL -> the URL to download from (required)
 *  mimeType -> the mime type of the file (required)
 *  forceDownload -> true = download the file, false = open in a new tab
 *  filename -> the name of the file to be downloaded, required if "forceDownload = true"
 */
export const openFileUrlInBrowser = (contentURL, mimeType, forceDownload, filename) => {
  if (!contentURL) throw Error('Parameter "content URL" is required.')

  if (!mimeType) throw Error('Parameter "mimeType" is required.')
  if (!(typeof mimeType === 'string')) throw Error('Parameter "mimeType" should be a string.')
  if (!/^[\S]+\/[\S]+$/.test(mimeType)) throw Error('Invalid "mimeType" format.')

  if (forceDownload === true) {
    if (!filename || !filename.trim()) throw Error('Parameter "filename" is required and cannot be empty when "forceDownload" is true.')
    if (!(typeof filename === 'string')) throw Error('Parameter "filename" must be a string.')
  } else if (!isMimeTypeSupportedByBrowser(mimeType)) throw Error('The file mime type cannot be opened by browser.')

  const link = document.createElement('a')
  link.href = contentURL
  link.style.display = 'none'

  if (forceDownload === true) {
    link.setAttribute('download', filename)
    // handling a Safari bug where it thinks _blank anchor are pop ups
    if (typeof link.download === 'undefined') link.target = '_blank'
  } else link.target = '_blank'

  document.body.appendChild(link)
  link.click()
  window.URL.revokeObjectURL(contentURL)
  document.body.removeChild(link)

  return true
}

/**
 * This function transforms raw file content data into a DataURL and either
 * opens it on the browser window or downloads it into the users device.
 *
 * Arguments:
 *  content -> the raw file content (required)
 *  mimeType -> the mime type of the file (required)
 *  forceDownload -> true = download the file, false = open in a new tab
 *  filename -> the name of the file to be downloaded, required if "forceDownload = true"
 */
export const openFileContentsInBrowser = (content, mimeType, forceDownload, filename) => {
  if (!content) throw Error('Parameter "content" is required.')

  if (!mimeType) throw Error('Parameter "mimeType" is required.')
  if (!(typeof mimeType === 'string')) throw Error('Parameter "mimeType" should be a string.')
  if (!/^[\S]+\/[\S]+$/.test(mimeType)) throw Error('Invalid "mimeType" format.')

  if (forceDownload === true) {
    if (!filename || !filename.trim()) throw Error('Parameter "filename" is required and cannot be empty when "forceDownload" is true.')
    if (!(typeof filename === 'string')) throw Error('Parameter "filename" must be a string.')
  } else if (!isMimeTypeSupportedByBrowser(mimeType)) throw Error('The file mime type cannot be opened by browsers.')

  const data = window.URL.createObjectURL(new Blob([content], { type: mimeType }))

  // Firefox has a bug and doesn't allow opening a data blob
  // https://bugzilla.mozilla.org/show_bug.cgi?id=1696174
  // thru a script activated link, so we use "window.open" instead
  if (!(forceDownload === true) && (navigator.userAgent.indexOf('Firefox')) !== -1) {
    const wnd = window.open(data, '_blank')
    // check if the window was blocked by the browser
    if (wnd === null) return false

    return true
  }

  const link = document.createElement('a')
  link.href = data
  link.style.display = 'none'

  if (forceDownload === true) {
    link.setAttribute('download', filename)
    // handling a Safari bug where it thinks _blank anchor are pop ups
    if (typeof link.download === 'undefined') link.target = '_blank'
  } else link.target = '_blank'

  document.body.appendChild(link)
  link.click()
  window.URL.revokeObjectURL(data)
  document.body.removeChild(link)

  return true
}

/**
 * Returns filtered list of allowed file types
 * @param {*} files
 * @param {*} allowedExt
 */
export const filterFileArrayByAllowed = (files, allowedExt) => {
  return files.filter(f => !!f.type && allowedExt.find(a => f.type.includes(a)))
}

/**
 * Format bytes as human-readable text.
 *
 * @param bytes Number of bytes.
 * @param si True to use metric (SI) units, aka powers of 1000. False to use
 *           binary (IEC), aka powers of 1024.
 * @param dp Number of decimal places to display.
 *
 * @return Formatted string.
 */
export const humanFileSize = (bytes, si = false, dp = 1) => {
  const thresh = si ? 1000 : 1024

  if (Math.abs(bytes) < thresh) return bytes + ' B'

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB']
  let u = -1
  const r = 10 ** dp

  do {
    bytes /= thresh
    ++u
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1)

  return bytes.toFixed(dp) + ' ' + units[u]
}
